import {
  CustomFieldResourceType,
  CustomFieldType,
  type CustomFieldDefinition,
} from "@repo/app-types/entities/customField";
import {
  buildFunnelOutcomeDefinition,
  greenRedYellowResultTypeCategory,
  isFunnelOutcomeDefinition,
  type FunnelOutcomeDefinition,
  type FunnelOutcomeStage,
  type OutcomeDefinition,
  type OutcomeGreenRedYellowResultCategory,
} from "@repo/app-types/entities/outcome";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Label } from "@repo/ui/Label/label";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useEffect, useMemo, useState, type FC } from "react";
import { twMerge } from "tailwind-merge";
import { useAllUserCustomFieldDefinitionsQuery } from "../../api/entities/userCustomField.js";
import { splitTrimmedCsv } from "../../helpers/utilities.js";

interface FunnelCustomFieldSelectProps {
  outcome: OutcomeDefinition | null;
  onUpdateOutcome: (outcome: OutcomeDefinition) => void;
  isReadOnly: boolean;
}

type FunnelStageCategory = Exclude<OutcomeGreenRedYellowResultCategory, "gray">;

const defaultStages: readonly FunnelOutcomeStage[] = [
  {
    id: "in_progress",
    name: "In progress",
    category: greenRedYellowResultTypeCategory.yellow,
    values: [],
  },
  {
    id: "completed",
    name: "Completed",
    category: greenRedYellowResultTypeCategory.green,
    values: [],
  },
];

const categoryOptions: readonly SimpleSelectOption<FunnelStageCategory>[] = [
  { value: greenRedYellowResultTypeCategory.yellow, label: "Yellow" },
  { value: greenRedYellowResultTypeCategory.red, label: "Red" },
  { value: greenRedYellowResultTypeCategory.green, label: "Green" },
] as const;

export const FunnelCustomFieldSelect: FC<FunnelCustomFieldSelectProps> = ({
  outcome,
  onUpdateOutcome,
  isReadOnly,
}) => {
  const { data: allCustomFieldDefinitions, isLoading } =
    useAllUserCustomFieldDefinitionsQuery();

  const funnelOutcome = isFunnelOutcomeDefinition(outcome)
    ? outcome
    : undefined;
  const [stageValuesTextById, setStageValuesTextById] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    if (!funnelOutcome) {
      setStageValuesTextById({});
      return;
    }

    setStageValuesTextById((current) =>
      Object.fromEntries(
        funnelOutcome.stages.map((stage) => [
          stage.id,
          current[stage.id] ?? stage.values.join(","),
        ]),
      ),
    );
  }, [funnelOutcome]);

  const eligibleCustomFieldDefinitions: readonly CustomFieldDefinition[] =
    useMemo(
      () =>
        (allCustomFieldDefinitions ?? []).filter(
          (def) =>
            def.resource_type === CustomFieldResourceType.CONTACT &&
            (def.field_type === CustomFieldType.Text ||
              def.field_type === CustomFieldType.Number),
        ),
      [allCustomFieldDefinitions],
    );

  const customFieldOptions: SimpleSelectOption<string>[] = useMemo(
    () =>
      eligibleCustomFieldDefinitions.map((def) => ({
        value: def.id,
        label: def.display_name,
      })),
    [eligibleCustomFieldDefinitions],
  );

  const commit = (definition: FunnelOutcomeDefinition): void => {
    onUpdateOutcome(definition);
  };

  const buildDefinition = ({
    customFieldDefinitionId = funnelOutcome?.custom_field_definition_id ??
      customFieldOptions[0]?.value,
    stages = funnelOutcome?.stages ?? defaultStages,
  }: {
    customFieldDefinitionId?: string;
    stages?: readonly FunnelOutcomeStage[];
  } = {}) => {
    if (!customFieldDefinitionId) {
      return undefined;
    }

    try {
      return buildFunnelOutcomeDefinition({
        customFieldDefinitionId,
        stages: ensureOneGreenStage(stages),
      });
    } catch {
      return undefined;
    }
  };

  const handleCustomFieldSelect = (customFieldId: string): void => {
    const definition = buildDefinition({
      customFieldDefinitionId: customFieldId,
    });
    if (definition) commit(definition);
  };

  const updateStages = (stages: readonly FunnelOutcomeStage[]): void => {
    const definition = buildDefinition({ stages });
    if (definition) commit(definition);
  };

  const addStage = (): void => {
    const stages = funnelOutcome?.stages ?? defaultStages;
    const id = uniqueSlug(
      "stage",
      stages.map((stage) => stage.id),
    );
    updateStages([
      ...stages,
      {
        id,
        name: `Stage ${stages.length + 1}`,
        category: greenRedYellowResultTypeCategory.yellow,
        values: [],
      },
    ]);
  };

  const updateStageCategory = (
    stageIndex: number,
    category: FunnelStageCategory,
  ): void => {
    if (!funnelOutcome) return;

    const currentStage = funnelOutcome.stages[stageIndex];
    if (!currentStage) return;

    const nextStages =
      category === greenRedYellowResultTypeCategory.green
        ? funnelOutcome.stages.map((stage, index) => ({
            ...stage,
            category:
              index === stageIndex
                ? greenRedYellowResultTypeCategory.green
                : stage.category === greenRedYellowResultTypeCategory.green
                  ? greenRedYellowResultTypeCategory.yellow
                  : stage.category,
          }))
        : replaceAt(funnelOutcome.stages, stageIndex, {
            ...currentStage,
            category,
          });

    updateStages(nextStages);
  };

  const hasNoEligibleFields =
    !isLoading && eligibleCustomFieldDefinitions.length === 0;

  return (
    <div className="space-y-6">
      <div>
        <SimpleSelect
          label="CRM status contact field"
          options={customFieldOptions}
          value={funnelOutcome?.custom_field_definition_id ?? null}
          onChange={handleCustomFieldSelect}
          placeholder="Select a custom field..."
          isDisabled={
            isReadOnly || isLoading || customFieldOptions.length === 0
          }
        />
      </div>

      {hasNoEligibleFields ? (
        <p className="text-xs text-destructive">
          No eligible contact text/number custom fields found.
        </p>
      ) : null}

      {funnelOutcome ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <Label size={Size.SMALL}>Funnel stages</Label>
            <Button
              variant={ButtonVariant.Secondary}
              size={Size.MEDIUM}
              icon={<IconPlus className="size-4" />}
              onClick={addStage}
              isDisabled={isReadOnly}
            >
              Add stage
            </Button>
          </div>

          <div className="overflow-hidden rounded-lg border border-border-light">
            <div className="hidden grid-cols-[minmax(9rem,1fr)_9rem_minmax(14rem,2fr)_2.5rem] items-center gap-3 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-600 lg:grid">
              <div>Stage</div>
              <div>Result</div>
              <div>CRM values</div>
              <div />
            </div>

            <div className="divide-y divide-border-light">
              {funnelOutcome.stages.map((stage, index) => (
                <div
                  key={stage.id}
                  className={twMerge(
                    "grid grid-cols-1 gap-3 px-3 py-3 lg:grid-cols-[minmax(9rem,1fr)_9rem_minmax(14rem,2fr)_2.5rem] lg:items-center",
                    stage.category === greenRedYellowResultTypeCategory.green &&
                      "bg-green-50/50",
                  )}
                >
                  <div className="min-w-0">
                    <div className="mb-1 lg:hidden">
                      <Label size={Size.SMALL}>Stage</Label>
                    </div>
                    <Input
                      id={`funnel-stage-name-${stage.id}`}
                      value={stage.name}
                      placeholder="Stage name"
                      isDisabled={isReadOnly}
                      onChange={(event) =>
                        updateStages(
                          replaceAt(funnelOutcome.stages, index, {
                            ...stage,
                            name: event.target.value,
                          }),
                        )
                      }
                      maxLength={100}
                    />
                  </div>

                  <div>
                    <div className="mb-1 lg:hidden">
                      <Label size={Size.SMALL}>Result</Label>
                    </div>
                    <SimpleSelect<FunnelStageCategory>
                      options={categoryOptions}
                      value={stage.category}
                      onChange={(category) =>
                        updateStageCategory(index, category)
                      }
                      isDisabled={isReadOnly}
                    />
                  </div>

                  <div className="min-w-0">
                    <div className="mb-1 lg:hidden">
                      <Label size={Size.SMALL}>CRM values</Label>
                    </div>
                    <Input
                      id={`funnel-stage-values-${stage.id}`}
                      value={
                        stageValuesTextById[stage.id] ?? stage.values.join(",")
                      }
                      placeholder="CRM_VALUE, ANOTHER_VALUE"
                      inputClassName="font-mono text-xs"
                      maxLength={1000}
                      isDisabled={isReadOnly}
                      onChange={(event) => {
                        const valuesText = event.target.value;
                        setStageValuesTextById((current) => ({
                          ...current,
                          [stage.id]: valuesText,
                        }));
                        updateStages(
                          replaceAt(funnelOutcome.stages, index, {
                            ...stage,
                            values: splitTrimmedCsv(valuesText),
                          }),
                        );
                      }}
                    />
                  </div>

                  <div className="flex justify-end">
                    <IconButton
                      variant={ButtonVariant.Secondary}
                      icon={<IconTrash className="size-4" />}
                      accessibleLabel={`Remove ${stage.name}`}
                      isDisabled={
                        isReadOnly || funnelOutcome.stages.length <= 1
                      }
                      onClick={() =>
                        updateStages(
                          funnelOutcome.stages.filter(
                            (_, stageIndex) => stageIndex !== index,
                          ),
                        )
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

function replaceAt<T>(
  values: readonly T[],
  index: number,
  value: T,
): readonly T[] {
  return values.map((current, currentIndex) =>
    currentIndex === index ? value : current,
  );
}

function ensureOneGreenStage(
  stages: readonly FunnelOutcomeStage[],
): readonly FunnelOutcomeStage[] {
  const greenStageIndexes = stages
    .map((stage, index) =>
      stage.category === greenRedYellowResultTypeCategory.green ? index : -1,
    )
    .filter((index) => index !== -1);

  if (greenStageIndexes.length === 1) {
    return stages;
  }

  const greenIndex = greenStageIndexes[0] ?? stages.length - 1;
  return stages.map((stage, index) => ({
    ...stage,
    category:
      index === greenIndex
        ? greenRedYellowResultTypeCategory.green
        : stage.category === greenRedYellowResultTypeCategory.green
          ? greenRedYellowResultTypeCategory.yellow
          : stage.category,
  }));
}

function uniqueSlug(base: string, existingIds: readonly string[]): string {
  const normalizedBase = slugify(base);
  let candidate = normalizedBase;
  let suffix = 2;
  while (existingIds.includes(candidate)) {
    candidate = `${normalizedBase}_${suffix}`;
    suffix += 1;
  }
  return candidate;
}

function slugify(value: string): string {
  return (
    value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "") || "stage"
  );
}
